import { useState, useCallback } from 'react';
import { useGetIdentity } from '@refinedev/core';
import { CategoriesService } from '../services/categoriesService';
import {
  Category,
  CreateCategoryData,
  UpdateCategoryData,
  CategoryWithTemplates,
  TodoTemplate,
  CreateTodoTemplateData,
  UpdateTodoTemplateData,
  TodoTemplateWithItems,
  TemplateItem,
  CreateTemplateItemData,
  UpdateTemplateItemData
} from '../types/todo';
import { useSupabase } from './useSupabase';

export const useCategoriesService = () => {
  const supabase = useSupabase();
  const { data: user } = useGetIdentity();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoriesService = new CategoriesService(supabase);

  const handleAsync = useCallback(async <T>(operation: () => Promise<T>): Promise<T | null> => {
    if (!user?.data.id) {
      setError('User not authenticated');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await operation();
      return result;
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.data.id]);

  // Categories operations
  const getCategories = useCallback(async (includePublic = true): Promise<CategoryWithTemplates[]> => {
    const result = await handleAsync(() => 
      categoriesService.getCategories()
    );
    return result || [];
  }, [categoriesService, handleAsync, user]);

  const getCategoryById = useCallback(async (id: string): Promise<CategoryWithTemplates | null> => {
    return await handleAsync(() => 
      categoriesService.getCategoryById(id)
    );
  }, [categoriesService, handleAsync, user]);

  const createCategory = useCallback(async (categoryData: CreateCategoryData): Promise<Category | null> => {
    return await handleAsync(() => 
      categoriesService.createCategory(categoryData, user.data.id)
    );
  }, [categoriesService, handleAsync, user]);

  const updateCategory = useCallback(async (id: string, categoryData: UpdateCategoryData): Promise<Category | null> => {
    return await handleAsync(() => 
      categoriesService.updateCategory(id, categoryData, user.data.id)
    );
  }, [categoriesService, handleAsync, user]);

  const deleteCategory = useCallback(async (id: string): Promise<boolean> => {
    const result = await handleAsync(() => 
      categoriesService.deleteCategory(id)
    );
    return result !== null;
  }, [categoriesService, handleAsync, user]);

  // Templates operations
  const getTemplatesByCategory = useCallback(async (categoryId: string): Promise<TodoTemplate[]> => {
    const result = await handleAsync(() => 
      categoriesService.getTemplatesByCategory(categoryId, user.data.id)
    );
    return result || [];
  }, [categoriesService, handleAsync, user]);

  const getTemplateById = useCallback(async (id: string): Promise<TodoTemplateWithItems | null> => {
    return await handleAsync(() => 
      categoriesService.getTemplateById(id, user.data.id)
    );
  }, [categoriesService, handleAsync, user]);

  const createTemplate = useCallback(async (templateData: CreateTodoTemplateData): Promise<TodoTemplate | null> => {
    return await handleAsync(() => 
      categoriesService.createTemplate(templateData, user.data.id)
    );
  }, [categoriesService, handleAsync, user]);

  const updateTemplate = useCallback(async (id: string, templateData: UpdateTodoTemplateData): Promise<TodoTemplate | null> => {
    return await handleAsync(() => 
      categoriesService.updateTemplate(id, templateData, user.data.id)
    );
  }, [categoriesService, handleAsync, user]);

  const deleteTemplate = useCallback(async (id: string): Promise<boolean> => {
    const result = await handleAsync(() => 
      categoriesService.deleteTemplate(id, user.data.id)
    );
    return result !== null;
  }, [categoriesService, handleAsync, user]);

  // Template items operations
  const getTemplateItems = useCallback(async (templateId: string): Promise<TemplateItem[]> => {
    const result = await handleAsync(() => 
      categoriesService.getTemplateItems(templateId)
    );
    return result || [];
  }, [categoriesService, handleAsync]);

  const createTemplateItem = useCallback(async (itemData: CreateTemplateItemData): Promise<TemplateItem | null> => {
    return await handleAsync(() => 
      categoriesService.createTemplateItem(itemData)
    );
  }, [categoriesService, handleAsync]);

  const updateTemplateItem = useCallback(async (id: string, itemData: UpdateTemplateItemData): Promise<TemplateItem | null> => {
    return await handleAsync(() => 
      categoriesService.updateTemplateItem(id, itemData)
    );
  }, [categoriesService, handleAsync]);

  const deleteTemplateItem = useCallback(async (id: string): Promise<boolean> => {
    const result = await handleAsync(() => 
      categoriesService.deleteTemplateItem(id)
    );
    return result !== null;
  }, [categoriesService, handleAsync]);

  const reorderTemplateItems = useCallback(async (templateId: string, itemIds: string[]): Promise<boolean> => {
    const result = await handleAsync(() => 
      categoriesService.reorderTemplateItems(templateId, itemIds)
    );
    return result !== null;
  }, [categoriesService, handleAsync]);

  // Utility operations
  const duplicateTemplate = useCallback(async (templateId: string, newName?: string): Promise<TodoTemplate | null> => {
    return await handleAsync(() => 
      categoriesService.duplicateTemplate(templateId, user.data.id, newName)
    );
  }, [categoriesService, handleAsync, user]);

  const searchTemplates = useCallback(async (query: string): Promise<TodoTemplateWithItems[]> => {
    const result = await handleAsync(() => 
      categoriesService.searchTemplates(query, user.data.id)
    );
    return result || [];
  }, [categoriesService, handleAsync, user]);

  return {
    // State
    loading,
    error,
    
    // Categories
    getCategories,
    getCategoryById,
    createCategory,
    updateCategory,
    deleteCategory,
    
    // Templates
    getTemplatesByCategory,
    getTemplateById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    
    // Template Items
    getTemplateItems,
    createTemplateItem,
    updateTemplateItem,
    deleteTemplateItem,
    reorderTemplateItems,
    
    // Utilities
    duplicateTemplate,
    searchTemplates,
    
    // Clear error
    clearError: () => setError(null)
  };
};
